class PortfolioLoader {
    constructor(apiBaseUrl, gridContainerSelector, itemTemplateSelector, nonce, origin, perPage = 12) {
        this.apiBaseUrl = apiBaseUrl;
        this.gridContainer = document.querySelector(gridContainerSelector);
        this.itemTemplate = document.querySelector(itemTemplateSelector)?.cloneNode(true);
        this.nonce = nonce;
        this.origin = origin;
        this.perPage = perPage;

        const urlParams = new URLSearchParams(window.location.search);

        this.initialParams = {
            filter: urlParams.get('filter') || null,
            search: urlParams.get('search') || null,
            sqf: urlParams.get('sqf') || null,
            cost: urlParams.get('cost') ||  null
,            order: urlParams.get('order') || null
        };

        if (!this.gridContainer || !this.itemTemplate) {
            console.error('PortfolioLoader not properly initialized.');
            this.hasMoreData = false;
            return;
        }

        this.currentPage = 2; // Start from page 2
        this.isLoading = false;
        this.hasMoreData = true;
        this.observer = null;
        this._activePlaceholder = null;

        this._setupObserver();

        // Start loading page 2 after a brief delay
        setTimeout(() => {
            this.loadMoreItems();
        }, 500);
    }

    _setupObserver() {
        const options = {
            root: null,
            rootMargin: '300px 0px', // Start loading early before user reaches item
            threshold: 0.1
        };
        this.observer = new IntersectionObserver(this._handleIntersection.bind(this), options);
    }

    _handleIntersection(entries, observer) {
        entries.forEach(entry => {
            if (entry.isIntersecting && this.hasMoreData && !this.isLoading) {
                observer.unobserve(entry.target);
                console.log('Observed item in view — loading more...');
                this.loadMoreItems();
            }
        });
    }

    _showLoadingPlaceholder() {
        const placeholder = document.createElement('div');
        placeholder.className = 'portfolio-loading-placeholder';
        placeholder.style.height = '300px';
        this.gridContainer.appendChild(placeholder);
        this._activePlaceholder = placeholder;
    }

    _removeLoadingPlaceholder() {
        if (this._activePlaceholder) {
            this._activePlaceholder.remove();
            this._activePlaceholder = null;
        }
    }

    async _fetchPortfolioData() {
        if (this.isLoading || !this.hasMoreData) return null;

        this.isLoading = true;
        const url = new URL(this.apiBaseUrl);
        url.searchParams.append('paged', this.currentPage);
        url.searchParams.append('per_page', this.perPage);
        for (const [key, value] of Object.entries(this.initialParams)) {
            if (value !== null) {
                url.searchParams.append(key, value);
            }
        }

        try {
            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-WP-Nonce': this.nonce,
                    'Origin': this.origin
                },
                credentials: 'same-origin'
            });

            if (!response.ok) {
                throw new Error(`API request failed: ${response.status}`);
            }

            const data = await response.json();
            this.isLoading = false;
            return data;
        } catch (error) {
            console.error('Error fetching portfolio data:', error);
            this.isLoading = false;
            this.hasMoreData = false;
            this.observer.disconnect();
            return null;
        }
    }

    _processData(data) {
        if (!data || !data.posts || data.posts.length === 0) {
            console.log('No more items to load.');
            this.hasMoreData = false;
            this.observer.disconnect();
                 document.querySelectorAll('.portfolio-loading-placeholder').forEach(el => el.remove());
            return;
        }

        this._renderItems(data.posts);

        this.currentPage++;
        if (data.total_pages && this.currentPage > data.total_pages) {
            console.log('All pages loaded.');
            this.hasMoreData = false;
            this.observer.disconnect();
             // Clean up any remaining loading placeholders
      
        }
    }

    _createItemElement(itemData) {
        if (!this.itemTemplate) return null;

        const newItem = this.itemTemplate.cloneNode(true);
        newItem.href = itemData.permalink;
        newItem.classList.add('portfolio-item');

        const bgImageDiv = newItem.querySelector('.x-bg-layer-lower-image');
        if (bgImageDiv) {
            bgImageDiv.style.backgroundImage = itemData.image_url
                ? `url(${itemData.image_url})`
                : 'none';
        }

        const titleElement = newItem.querySelector('.x-text-content-text-primary');
        if (titleElement) {
            let titleTextNode = null;
            for (let child of titleElement.childNodes) {
                if (child.nodeType === Node.TEXT_NODE && child.textContent.trim() !== '') {
                    titleTextNode = child;
                    break;
                }
            }

            if (titleTextNode) {
                titleTextNode.nodeValue = itemData.title;
            } else {
                const citySpan = titleElement.querySelector('.city-span');
                const cityHTML = citySpan ? citySpan.outerHTML : '<span class="city-span"></span>';
                titleElement.innerHTML = `${itemData.title}<br>${cityHTML}`;
            }
        }

        const citySpan = newItem.querySelector('.city-span');
        if (citySpan) {
            citySpan.textContent = itemData.city || '';
        }

        newItem.classList.remove('awaiting-observation');

        // Trigger fade-in animation
        requestAnimationFrame(() => {
            newItem.classList.add('loaded');
        });

        return newItem;
    }

    _renderItems(itemsData) {
        const fragment = document.createDocumentFragment();
        let lastObservableItem = null;

        itemsData.forEach(itemData => {
            const itemElement = this._createItemElement(itemData);
            if (itemElement) {
                fragment.appendChild(itemElement);
                if (itemData.observe) {
                    lastObservableItem = itemElement;
                }
            }
        });

        this._removeLoadingPlaceholder();
        this.gridContainer.appendChild(fragment);

        if (lastObservableItem && this.hasMoreData) {
            console.log(`Observing next: "${itemsData.find(p => p.observe)?.title || 'Unnamed'}"`);
            this.observer.observe(lastObservableItem);
        } else if (!lastObservableItem && this.hasMoreData) {
            console.warn('No observable items found for next trigger.');
        }
    }

    async loadMoreItems() {
        if (!this.hasMoreData || this.isLoading) return;

        this._showLoadingPlaceholder();

        await new Promise(resolve => setTimeout(resolve, 100)); // Throttle a little

        const newData = await this._fetchPortfolioData();
 
        if (newData.out_of_posts) {
            this.hasMoreData = false;
            this.observer.disconnect();
        }
        
        if (newData) {
            this._processData(newData);
        }
    }
}

export default PortfolioLoader;
